import { defineComponent } from '~/utils/alpine_types'

type InAppPurchaseSubject = {
  in_app_purchase_id: number
  need_supervision: boolean
  lesson_type_subject_id: number | null
  lesson_count: number
  lesson_questions: LessonQuestion[]
}

type LessonTypeSubject = {
  id: number
  name: string
}

type InAppPurchase = {
  id: number
  lesson_type_subjects: LessonTypeSubject[]
}

type Question = {
  id: number
  name: string
  maximum_score: number
  sort_order: number
}

type LessonQuestion = {
  id: number | null
  quiz_question_id: number
  sort_order: number
  _destroy: boolean | undefined
}

type Args = {
  subject: InAppPurchaseSubject
  inAppPurchases: InAppPurchase[]
  questions: Question[]
}

export const inAppPurchaseSubject = defineComponent((args: Args) => ({
  subject: args.subject,
  inAppPurchases: args.inAppPurchases,
  questions: args.questions,

  get lessonQuestions() {
    return this.subject.lesson_questions.sort((a, b) => a.sort_order - b.sort_order)
  },

  get activeLessonQuestions() {
    return this.lessonQuestions.filter((q) => !q._destroy)
  },

  get lessonTypeSubjects(): LessonTypeSubject[] {
    const inAppPurchase = this.inAppPurchases.find(({ id }) => id === this.subject.in_app_purchase_id)
    const subjects = inAppPurchase?.lesson_type_subjects || []

    // Select first radio button when the selectable options change
    if (!subjects.some(({ id }) => id === this.subject.lesson_type_subject_id)) {
      this.subject.lesson_type_subject_id = subjects[0]?.id || null
    }

    return subjects
  },

  get maximumTestScore(): number {
    return this.activeLessonQuestions.reduce(
      (acc, lessonQuestion) =>
        acc + (this.questions.find(({ id }) => id === lessonQuestion.quiz_question_id)?.maximum_score || 0),
      0,
    )
  },

  init() {
    this.$watch('lessonQuestions', () => this.$dispatch('questions-changed'))
  },

  toggleQuestion(id: number) {
    let idx = this.lessonQuestions.findIndex((q) => q.quiz_question_id === id)
    const lessonQuestion = this.lessonQuestions[idx]
    const newIdx = this.activeLessonQuestions.length + 1

    if (!lessonQuestion) {
      this.lessonQuestions.push({ id: null, quiz_question_id: id, sort_order: newIdx, _destroy: false })
      return
    }

    // Flag existing records for removal or fully remove new ones
    if (lessonQuestion.id) {
      lessonQuestion._destroy = !lessonQuestion._destroy
    } else {
      this.lessonQuestions.splice(idx, 1)
    }

    // Re-enabled questions get reset to the end, removing a question decrements subsequent questions' sort numbers
    if (lessonQuestion && !lessonQuestion._destroy) {
      lessonQuestion.sort_order = newIdx
    } else {
      this.activeLessonQuestions.forEach((q) => q.sort_order > lessonQuestion.sort_order && q.sort_order--)
    }
  },
}))
